/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  WbMarkdownHelperText,
  WbTextField,
} from '@agilelab/plugin-wb-platform';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { isHidden } from '../../utils';
import { EntityNamePickerProps } from './schema';
import { useCallback, useMemo } from 'react';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

/**
 * EntityName Picker
 */
export const EntityNamePicker = (props: EntityNamePickerProps) => {
  const {
    onChange,
    required,
    schema: { title = 'Name', description = 'Unique name of the component' },
    rawErrors,
    formData,
    uiSchema,
    idSchema,
    placeholder,
    formContext,
  } = props;

  const autoFocus = uiSchema['ui:autofocus'];
  const maxLength = uiSchema['ui:options']?.maxLength;
  const { debounceMs } = useFormExtraConfigContext();

  // Check if dependency manager is available
  const dependencyManager = formContext?.dependencyManager;

  // Extract canonical field path from RJSF's idSchema
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id);
  }, [idSchema]);

  // Handle changes and notify dependency manager
  const handleChange = useCallback(
    (value: string | undefined) => {
      // Notify dependency manager so fields that depend on name are recomputed, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value === '' ? undefined : value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <>
      <WbTextField
        id={idSchema?.$id}
        style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
        label={title}
        placeholder={placeholder}
        disabled={!(uiSchema['ui:options']?.allowArbitraryValues ?? true)}
        required={required}
        value={formData ?? ''}
        debounceMs={debounceMs}
        onChange={({ target: { value } }) => {
          handleChange(value);
        }}
        error={rawErrors?.length > 0 && !formData}
        inputProps={{ autoFocus, maxLength }}
      />
      <WbMarkdownHelperText helperText={description} />
    </>
  );
};
